/**
 * Sanitizes error messages from backend to show user-friendly messages
 * Never exposes raw backend error messages to users
 * 
 * @param {Error} error - The error object from axios/API call
 * @param {string} defaultMessage - Default message if error type can't be determined
 * @returns {string} - User-friendly error message
 */
export const getErrorMessage = (error, defaultMessage = 'Failed to complete the request') => {
    // Log the actual error for debugging (only in development)
    if (import.meta.env.VITE_DEBUG_MODE === 'true' || import.meta.env.VITE_NODE_ENV !== 'production') {
        console.error('Error details:', {
            message: error?.message,
            response: error?.response?.data,
            status: error?.response?.status,
            code: error?.code
        });
    }

    // Network errors
    if (error?.code === 'ERR_NETWORK' || error?.message?.includes('Network Error')) {
        return 'Failed to connect to server. Please check your internet connection and try again.';
    }

    // Timeout errors
    if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
        return 'Request timed out. Please try again.';
    }

    // HTTP status code based messages
    if (error?.response?.status) {
        const status = error.response.status;
        
        switch (status) {
            case 400:
                return 'Failed to process request. Please check your input and try again.';
            case 401:
                return 'Authentication failed. Please login again.';
            case 403:
                return 'Access denied. You do not have permission to perform this action.';
            case 404:
                return 'Resource not found. Please try again.';
            case 409:
                return 'Conflict detected. This action cannot be completed.';
            case 422:
                return 'Invalid data provided. Please check your input and try again.';
            case 429:
                return 'Request failed.Please wait a moment and try again.';
            case 500:
                return 'Server error occurred. Please try again later.';
            case 502:
            case 503:
                return 'Service temporarily unavailable. Please try again later.';
            case 504:
                return 'Request timed out. Please try again.';
            default:
                return defaultMessage;
        }
    }

    // If we have a response but no status, or other error
    if (error?.response) {
        return defaultMessage;
    }

    // Generic error
    return defaultMessage;
};

/**
 * Gets a context-specific error message
 * 
 * @param {Error} error - The error object
 * @param {string} action - The action that failed (e.g., 'login', 'fetch data', 'update settings')
 * @returns {string} - User-friendly error message
 */
export const getActionErrorMessage = (error, action) => {
    const defaultMessage = `Failed to ${action}. Please try again.`;
    return getErrorMessage(error, defaultMessage);
};


